import React, { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import AccordionBtn from 'components/commercetools-ui/atoms/accordion';
import Button from 'components/commercetools-ui/atoms/button';
import Input, { InputProps } from 'components/commercetools-ui/atoms/input';
import Link from 'components/commercetools-ui/atoms/link';
import Message from 'components/commercetools-ui/atoms/message';
import { useFormat } from 'helpers/hooks/useFormat';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useValidate from 'helpers/hooks/useValidate';
import * as screensizes from 'helpers/utils/screensizes';
import { LinkReference } from 'types/reference';
import { useAccount } from 'frontastic';
import Image, { ImageProps } from 'frontastic/lib/image';
import Column from './atoms/column';
import FooterBottom from './footer-bottom';
import { AGPerks, FooterLink, FooterSection, SocialMedia, StoreOpeningHours } from './types';
import useLocale from 'helpers/hooks/useLocale';

export interface Props {
  columns: FooterSection[];
  logo?: ImageProps;
  socialMedia?: SocialMedia[];
  cartCheckoutData?: FooterSection;
  contactData?: FooterSection;
  storeOpeningHours?: StoreOpeningHours;
  agPerks?: AGPerks;
}

const Footer: React.FC<Props> = ({
  columns,
  logo,
  socialMedia,
  cartCheckoutData,
  contactData,
  storeOpeningHours,
  agPerks,
}) => {
  const [isBiggerThanDesktopSize] = useMediaQuery(screensizes.desktop);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [message, setMessage] = useState({ errorMessage: '', severity: '' });
  const [isLoading, setIsLoading] = useState(false);
  const { newsletterSignup } = useAccount();
  const [emailData, setEmailData] = useState('');
  const { formatMessage } = useFormat();
  const { validateEmail } = useValidate();
  const pathname = usePathname();
  const isCartCheckout = pathname === '/checkout/' || pathname === '/cart/';
  const { locale } = useLocale();
  let newsletterLocale = 'americangolf-GB';

  if (locale === 'en_EU') {
    newsletterLocale = 'americangolf-EU';
  }

  const handleNewspaperSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (emailData.length === 0 || !validateEmail(emailData)) {
      setMessage({
        severity: 'error',
        errorMessage:
          'An error occured so we were unable to subscribe you to our newsletter. Please attend the hint below.',
      });
      return;
    }
    try {
      setIsLoading(true);
      const res = await newsletterSignup(emailData, newsletterLocale);

      if (res.statusCode !== 200) {
        setMessage({
          severity: 'error',
          errorMessage: 'Something went wrong while trying to subscribe to our newsletter.',
        });
        return;
      }
      setMessage({
        severity: 'success',
        errorMessage: 'You are successfully subscribed to our newsletter',
      });
    } catch (error) {
      console.error('Failed to signup to newsletter', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailData(e.target.value);
  };

  const emailField: InputProps = {
    label: formatMessage({ id: 'email', defaultMessage: 'Email' }),
    name: 'email',
    errorMessage: 'This field is required.',
    validation(value) {
      return validateEmail(value);
    },
    required: true,
  };

  useEffect(() => {
    setIsPageLoaded(true);
  }, []);

  return (
    <>
      {isPageLoaded && (
        <footer aria-labelledby="footer-heading" className="w-full bg-[#f5f4f3]">
          {isCartCheckout && (
            <div className="mx-auto hidden max-w-[104rem] border-b-[1px] lg:block lg:px-20 xl:px-48">
              <div className="mx-auto w-full pb-20 pt-25">
                <ul className="flex list-none justify-between">
                  {agPerks?.links?.map((link: FooterLink, linkIndex: number) => (
                    <li className="text-13 font-light text-input-focusBorder" key={`${linkIndex}`}>
                      <i className="icon icon-check mr-5" />
                      {link.text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          <div className="mx-auto w-full max-w-[104rem] justify-center p-0 md:flex md:p-5">
            {!isCartCheckout && (
              <div className="relative mx-auto h-[420px] px-40 py-40 sm:px-24 sm:py-0 md:order-last md:flex-1 lg:mr-20 lg:bg-cover">
                <img
                  src="/images/footer_bg.jpg"
                  alt="Footer background"
                  loading="lazy"
                  className="absolute left-0 top-0 z-0 h-full w-full object-cover"
                />
                <div className="relative flex h-full flex-col items-center justify-center gap-25">
                  <h2 id="footer-heading" className="z-10 text-center text-20 font-semibold text-white">
                    {formatMessage({
                      id: 'footer.heading',
                      defaultMessage: 'Sign up for the latest news & offers or follow us on',
                    })}
                  </h2>
                  <div className="flex space-x-30">
                    {socialMedia &&
                      socialMedia?.length > 0 &&
                      socialMedia?.map((item, idx) => (
                        <Link
                          key={item?.logo?.media?.name ?? idx}
                          target={`${item?.reference?.openInNewWindow ? '_blank' : '_self'}`}
                          link={(item?.reference as LinkReference)?.link || ''}
                        >
                          <Image key={idx} src={item?.logo?.media?.file} className="h-26 w-26" />
                        </Link>
                      ))}
                  </div>

                  <div className="w-[100%] sm:ms-[50px] sm:w-[350px]">
                    {message.severity === 'error' && (
                      <Message severity={message.severity} message={message.errorMessage} />
                    )}
                    {message.severity === 'success' ? (
                      <Message severity={message.severity} message={message.errorMessage} />
                    ) : (
                      <form className="mb-7  ms-16 mt-21 max-w-[95%] md:max-w-[80%]" onSubmit={handleNewspaperSignup}>
                        <div className="relative h-[50px]">
                          <div>
                            <Input
                              className="border-none"
                              onChange={handleInputChange}
                              name="email"
                              value={emailData}
                              placeholder="Enter your email address"
                              isFooter={true}
                              labelPosition="inline"
                              isValid={emailField.validation && emailField?.validation(emailData)}
                            />
                          </div>
                          <div className="absolute right-0 top-0">
                            <Button className="rounded-l-none font-semibold" variant="primary" loading={isLoading}>
                              Sign up
                            </Button>
                          </div>
                        </div>
                      </form>
                    )}
                  </div>

                  <p className="text-14 font-light text-white">
                    Please{' '}
                    <a
                      href="/services/content/privacy-policy.html"
                      target="_blank"
                      className="font-light text-white underline"
                      rel="noreferrer"
                    >
                      click here
                    </a>{' '}
                    to read our privacy policy
                  </p>
                </div>
              </div>
            )}
            {isBiggerThanDesktopSize ? (
              <>
                {isCartCheckout ? (
                  <div className="mx-auto mb-15 mt-30 flex w-full px-24 py-58 lg:mb-0 lg:mt-0 lg:flex-6 lg:px-20 xl:px-48">
                    <div className="flex w-[825px] max-w-[66%] justify-between">
                      <div>
                        <React.Fragment>
                          <h2 className="pb-15 pt-25 text-23 font-bold uppercase">{cartCheckoutData?.name}</h2>
                          <ul className="list-none">
                            {cartCheckoutData?.links?.map((link, index) => (
                              <li key={index} className="py-5">
                                <a
                                  href={link?.url}
                                  target={`${link?.openInNewWindow ? '_blank' : '_self'}`}
                                  className="cursor-pointer text-14 font-medium hover:text-input-focusBorder"
                                >
                                  {formatMessage({
                                    id: `footer.${link?.text.replaceAll(' ', '.')}`,
                                    defaultMessage: link?.text,
                                  })}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </React.Fragment>
                      </div>
                      <div className="w-[35%]">
                        <>
                          <React.Fragment>
                            <h2 className="pb-15 pt-25 text-23 font-bold uppercase">{contactData?.name}</h2>
                            <ul>
                              {contactData?.links?.map((link, idx) => (
                                <React.Fragment key={idx}>
                                  {idx === 0 ? (
                                    <li className="py-5">
                                      <a className="flex items-end gap-2 text-18 font-medium" href={link?.url}>
                                        <i className="icon icon-phone mr-7 text-20" />
                                        {link?.text}
                                      </a>
                                    </li>
                                  ) : (
                                    <li className="py-5">
                                      <a
                                        className={`${idx === 2 ? 'text-14' : 'text-18'} font-medium`}
                                        href={link?.url}
                                      >
                                        {link?.text}
                                      </a>
                                    </li>
                                  )}
                                </React.Fragment>
                              ))}
                            </ul>
                          </React.Fragment>

                          <ul>
                            <ul className="py-5 text-14 font-light text-input-focusBorder">
                              {storeOpeningHours?.links?.map((link, linkIndex) => (
                                <li key={linkIndex}>
                                  {formatMessage({
                                    id: `footer.${link?.text?.replaceAll(' ', '.') || ''}`,
                                    defaultMessage: link?.text || '',
                                  })}
                                </li>
                              ))}
                            </ul>
                          </ul>
                        </>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mb-15 mt-30 flex w-full justify-center px-4 py-24 lg:mb-0 lg:mt-0 lg:flex-6 lg:px-20 xl:px-48">
                    <div className="grid w-full grid-cols-4 gap-4">
                      {columns?.map((column, index) => (
                        <div key={index}>
                          <Column
                            header={column.name}
                            links={column.links ?? []}
                            className="lg:items flex flex-col items-center pr-20"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <ul className="mb-15 mt-30 flex flex-col p-25 md:flex-1 lg:mb-0 lg:mt-0 lg:hidden">
                {isCartCheckout ? (
                  <>
                    <li key="ag.help" className="border-t border-neutral-300">
                      <AccordionBtn
                        key={cartCheckoutData?.name}
                        variant="plusAndMinus"
                        closedSectionTitle={cartCheckoutData?.name ?? ''}
                        openSectionTitle={cartCheckoutData?.name}
                        buttonClassName="h-48 text-14 py-15 px-12 text-input-focusBorder uppercase text-18 font-bold text-gray-600 border-b border-t"
                        iconClassName="text-input-focusBorder text-gray-600"
                      >
                        <div className="mb-16 ml-20 flex flex-row text-14 md:ml-24" key="ag.help.items">
                          <ul className="list-none">
                            {cartCheckoutData?.links?.map((link, index) => (
                              <li key={index} className="py-5">
                                <a
                                  href={link?.url}
                                  target={`${link?.openInNewWindow ? '_blank' : '_self'}`}
                                  className="cursor-pointer text-14 font-medium hover:text-input-focusBorder"
                                >
                                  {formatMessage({
                                    id: `footer.${link?.text.replaceAll(' ', '.')}`,
                                    defaultMessage: link?.text,
                                  })}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </AccordionBtn>
                    </li>
                    <li key="ag.contact.us" className=" border-neutral-300">
                      <AccordionBtn
                        key={contactData?.name}
                        variant="plusAndMinus"
                        closedSectionTitle={contactData?.name ?? ''}
                        openSectionTitle="Contact Us"
                        buttonClassName="h-48 text-14 py-15 px-12 border-b text-input-focusBorder uppercase text-18 font-bold text-gray-600"
                        iconClassName="text-input-focusBorder text-gray-600"
                      >
                        <div>
                          <ul className="ml-20 list-none">
                            {contactData?.links?.map((link, idx) => (
                              <React.Fragment key={idx}>
                                {idx === 0 ? (
                                  <li className="py-5">
                                    <a className="flex items-end gap-2 text-18 font-medium" href={link?.text}>
                                      <i className="icon icon-phone mr-7 text-20" />
                                      {link?.text}
                                    </a>
                                  </li>
                                ) : (
                                  <li className="py-5">
                                    <a className={`${idx === 2 ? 'text-14' : 'text-18'} font-medium`} href={link?.text}>
                                      {link?.text}
                                    </a>
                                  </li>
                                )}
                              </React.Fragment>
                            ))}
                          </ul>
                          <ul className="ml-20">
                            <ul className="py-5 text-14 font-light text-input-focusBorder">
                              {storeOpeningHours?.links?.map((link, linkIndex) => (
                                <li key={linkIndex}>
                                  {formatMessage({
                                    id: `footer.${link?.text?.replaceAll(' ', '.') || ''}`,
                                    defaultMessage: link?.text || '',
                                  })}
                                </li>
                              ))}
                            </ul>
                          </ul>
                        </div>
                      </AccordionBtn>
                    </li>
                  </>
                ) : (
                  <>
                    {columns?.map((column, index) => (
                      <li key={index} className="order-neutral-300">
                        <AccordionBtn
                          variant="plusAndMinus"
                          closedSectionTitle={column.name ?? ''}
                          openSectionTitle={column.name}
                          buttonClassName="h-48 text-14 px-12 border-b border-t py-15 text-input-focusBorder uppercase text-18 font-bold text-gray-600"
                          iconClassName="text-input-focusBorder text-gray-600"
                        >
                          <div className="mb-16 ml-20 flex flex-row text-14 md:ml-24" key={index}>
                            <Column links={column.links ?? []} className="px-0" />
                          </div>
                        </AccordionBtn>
                      </li>
                    ))}
                  </>
                )}
              </ul>
            )}
          </div>

          <FooterBottom socialMedia={socialMedia} logo={logo} />
        </footer>
      )}
    </>
  );
};

export default Footer;
